// Fretboard and piano visualizers (§5.5): active notes follow playback or the
// cursor; the rest of the bar is shown as context. Touch input enters notes.

import { useMemo } from 'react';
import { eventsAt, enterPitch, trackKeyAt, type Score, type BeatRef } from '@simplenotation/core';
import { midiOf, spellMidi, tabToMidi, noteLabel, type NoteNameSystem, type KeySig, type Pitch } from '@simplenotation/theory';
import { useStore, edit, setView } from '../state/store';
import { FretboardCanvas, PianoCanvas, type VizNote } from '../viz';

/** Labels use the written spelling (B♭ stays B♭), not a re-spelling of the MIDI number. */
function label(p: Pitch, names: NoteNameSystem, key: KeySig): string {
  return noteLabel(p, names === 'none' ? 'abc' : names, key);
}

function barNotes(score: Score, ref: Pick<BeatRef, 'track' | 'bar'>, names: NoteNameSystem, key: KeySig, exclude?: string): VizNote[] {
  const t = score.tracks[ref.track];
  const out: VizNote[] = [];
  for (const v of t.bars[ref.bar]?.voices ?? []) {
    for (const b of v) {
      if (b.rest || b.id === exclude) continue;
      for (const n of b.notes) out.push({ midi: midiOf(n.pitch) + t.writtenToSounding, string: n.string, fret: n.fret, label: label(n.pitch, names, key) });
    }
  }
  return out;
}

export function Visualizers() {
  const view = useStore((s) => s.view);
  const ed = useStore((s) => s.ed);
  const playing = useStore((s) => s.playback.playing);
  const tick = useStore((s) => s.playback.tick);
  const timeline = useStore((s) => s.timeline);
  const touch = view.touchInput;
  const track = ed.score.tracks[ed.cursor.track];
  const key = trackKeyAt(ed.score, ed.cursor.track, ed.cursor.bar);

  const { active, context } = useMemo(() => {
    const names = view.noteNames;
    if (playing) {
      const evs = eventsAt(timeline, tick).filter((e) => e.track === ed.cursor.track);
      const bar = evs[0]?.barIdx ?? timeline.bars.find((b) => b.startTick <= tick && tick < b.startTick + b.length)?.barIdx ?? ed.cursor.bar;
      const pitchOf = new Map<string, Pitch>();
      for (const e of evs) {
        for (const v of track.bars[e.barIdx]?.voices ?? []) for (const b of v) for (const n of b.notes) if (n.id === e.noteId) pitchOf.set(e.noteId, n.pitch);
      }
      return {
        active: evs.map((e) => {
          const p = pitchOf.get(e.noteId);
          return { midi: e.midi, string: e.string, fret: e.fret, label: p ? label(p, names, key) : undefined };
        }),
        context: barNotes(ed.score, { track: ed.cursor.track, bar }, names, key),
      };
    }
    const c = ed.cursor;
    const beat = ed.score.tracks[c.track]?.bars[c.bar]?.voices[c.voice]?.[c.beat];
    const act = beat && !beat.rest ? beat.notes.map((n) => ({ midi: midiOf(n.pitch) + track.writtenToSounding, string: n.string, fret: n.fret, label: label(n.pitch, names, key) })) : [];
    return { active: act, context: barNotes(ed.score, c, names, key, beat?.id) };
  }, [playing, tick, timeline, ed, track, view.noteNames, key]);

  const range = useMemo((): [number, number] => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const b of track.bars) for (const v of b.voices) for (const x of v) for (const n of x.notes) {
      const m = midiOf(n.pitch) + track.writtenToSounding;
      lo = Math.min(lo, m);
      hi = Math.max(hi, m);
    }
    return Number.isFinite(lo) ? [lo, hi] : [48, 83];
  }, [track]);

  const showFret = view.showFretboard && !!track.tuning;
  if (!showFret && !view.showPiano) return null;
  const opts = { noteNames: view.noteNames === 'none' ? ('abc' as const) : view.noteNames, key, touchInput: touch };

  const pickFret = (string: number, fret: number) => {
    const sounding = tabToMidi(string, fret, track.tuning!, track.capo ?? 0);
    const pitch = spellMidi(sounding - track.writtenToSounding, key.fifths);
    edit((st) => {
      const c = st.cursor;
      const b = st.score.tracks[c.track].bars[c.bar].voices[c.voice]?.[c.beat];
      return enterPitch({ ...st, cursor: { ...c, string } }, pitch, !!b && !b.rest, { string, fret });
    }, { label: 'Fretboard note' });
  };
  const pickKey = (midi: number) => {
    const pitch = spellMidi(midi - track.writtenToSounding, key.fifths);
    edit((st) => {
      const c = st.cursor;
      const b = st.score.tracks[c.track].bars[c.bar].voices[c.voice]?.[c.beat];
      return enterPitch(st, pitch, !!b && !b.rest);
    }, { label: 'Piano note' });
  };

  return (
    <div className="visualizers">
      {showFret && (
        <div className="viz-panel">
          <FretboardCanvas
            instrument={{ tuning: track.tuning!, capo: track.capo, name: track.name }}
            active={active}
            context={context}
            options={{ ...opts, leftHanded: view.leftHanded }}
            onPick={touch ? pickFret : undefined}
          />
          <button className="viz-close" aria-label="Hide fretboard" onClick={() => setView({ showFretboard: false })}>
            ×
          </button>
        </div>
      )}
      {view.showPiano && (
        <div className="viz-panel">
          <PianoCanvas range={range} active={active} context={context} options={opts} onPick={touch ? pickKey : undefined} />
          <button className="viz-close" aria-label="Hide piano" onClick={() => setView({ showPiano: false })}>
            ×
          </button>
        </div>
      )}
    </div>
  );
}
